import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails,
  IconButton,
  makeStyles,
  Theme,
  Avatar,
  Grid,
  CircularProgress,
  useTheme,
  useMediaQuery } from '@material-ui/core';
import { AvatarGroup } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useHistory } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import { EntrepreneurProfileBody, InvestorMatchOutput, MatchListType, ReferralStatus, Role } from '../../types/types';
import Button from '../Button';
import { StyledText } from '../StyledText';
import LinkedinIcon from '../LinkedinIcon';
import ConnectorTable from './ConnectorTable';
import StatusChip from '../StatusChip';
import InvestmentCriteriaTable from './InvestmentCriteriaTable';
import DetectLinks from '../DetectLinks';
import { MatchedInvestorTabPathState } from '../../types/router';


const useAccordionStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: 0,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.border.main,
    '& .MuiAccordionSummary-content': {
      paddingLeft: 25
    },
    '& .MuiAccordionSummary-root': {
      flexDirection: 'row-reverse'
    },
    '& .MuiAccordionSummary-expandIcon': {
      height: 26,
      width: 26,
      backgroundColor: '#F0F0F0',
      '& svg': {
        fill: theme.palette.grey
      }
    },
    '& .Mui-expanded .MuiAccordionSummary-expandIcon': {
      height: 26,
      width: 26,
      backgroundColor: theme.palette.secondary.main,
      '& svg': {
        fill: 'white'
      }
    },
    '&:before ': {
      display: 'none'
    }
  }
}));

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: 0
  },
  summary: {
    margin: 0
  },
  heading: {
    fontSize: theme.typography.pxToRem(15)
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  column: {
    flexBasis: '33.33%'
  },
  detail: {
    display: 'flex',
    flexDirection: 'column'
  },
  avatar: {
    borderStyle: 'none'
  },
  itemContent: {
    padding: '0px 20px'
  },
  itemContentFlex: {
    padding: '0px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  itemContentFlexLeft: {
    padding: '0px 0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flexStart'
  },
  matchPercent: {
    color: theme.palette.primary.main
  },
  matchSpan: {
    padding: '0px 10px',
    fontWeight: theme.typography.fontWeightLight,
    fontSize: 16
  },
  investorNameBox: {
    fontWeight: theme.typography.fontWeightRegular,
    padding: '0px 20px'
  },
  connectorFlexRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  }
}));

interface InvestorAccordionProps {
  name: string
  matchListType: MatchListType
  investor: InvestorMatchOutput
  entrepreneurProfile?: EntrepreneurProfileBody | undefined
  referralHandler: (investor: InvestorMatchOutput) => void
  refreshHandler: () => void
}

const InvestorAccordion: React.FC<InvestorAccordionProps> = (props) => {
  const { name, investor, entrepreneurProfile, matchListType, referralHandler, refreshHandler } = props;
  const styles = useStyles();
  const history = useHistory<MatchedInvestorTabPathState | undefined>();
  const pathState = history.location.state;
  const { userInfo, getAccessToken } = useUser();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const accordionStyles = useAccordionStyles();

  // use investor contact name as acoordion key
  const investorName = investor.contactName.replace(/\s+/g, '-').toLowerCase();
  const matchScore = Math.round((investor.matchScore || 0) * 100);

  const isExpanded = useMemo(() => {
    if (pathState && pathState.expandedList) {
      return pathState.expandedList.filter((id) => id === investor.id).length > 0;
    } else {
      return false;
    }
  }, [pathState, investor.id]);

  const statusToShow = useMemo(() => {
    let statusToReturn = investor.referralStatus || ReferralStatus.Pending;
    const connectors = investor.connectorList;
    if (connectors !== undefined) {
      const referred = connectors.find((conn) => {
        const { referralData } = conn;
        if (referralData) {
          const { status } = referralData;
          return status === ReferralStatus.Pending || status === ReferralStatus.Accepted || status === ReferralStatus.Declined;
        }
        return false;
      });
      if (referred && referred.referralData) {
        statusToReturn = (referred.referralData.status as ReferralStatus);
      }
    }
    return statusToReturn;
  }, [investor]);

  const handlePagination = (id: string) => (e: React.MouseEvent) => {
    // prevent event propagate further
    const target = e.target as HTMLElement;
    e.stopPropagation();
    if (id === investorName) {
      if (target.tagName === 'P' || target.tagName === 'DIV') {
        toggleExpandedState();
      }
    }
  };

  const handleViewProfile = () => {
    history.push(`/investor/${investor.id}`, { from: window.location.pathname });
  };

  const handleReferralClick = () => {
    referralHandler(investor);
  };

  const handleLinkedin = () => {
    window.open(investor.linkedinProfile, '_blank');
  };

  const toggleExpandedState = useCallback(() => {
    const expanded = pathState?.expandedList || [];
    if (isExpanded) {
      const removed = expanded.filter((id) => id !== investor.id);
      history.replace({
        pathname: history.location.pathname,
        state: { ...pathState, expandedList: removed }
      });
    } else {
      const added = [...expanded, investor.id];
      history.replace({
        pathname: history.location.pathname,
        state: { ...pathState, expandedList: added }
      });
    }
  }, [isExpanded, history, investor.id, pathState]);

  const extendedRefreshHandler = useCallback(() => {
    if (isExpanded) {
      toggleExpandedState();
    }
    refreshHandler();
  }, [isExpanded, refreshHandler, toggleExpandedState]);

  if (!userInfo || !investor) return <CircularProgress />;

  const ConnectorInfoGrid = (
    <Grid item xs container alignItems='center'>
      <Grid item>
        <div className={styles.connectorFlexRow}>
          <StyledText noWrap style={{ margin: '0 0px', textAlign: 'right', width: '120px' }}>
            {`${investor.connectorList?.length || 0} ${(investor.connectorList?.length || []) > 1 ? 'connectors' : 'connector'}`}
          </StyledText>
          <div className={styles.itemContent}>
            <AvatarGroup max={isSmallScreen ? 2 : 5} style={{ display: 'flex', justifyContent: 'flex-start', flexGrow: 1 }}>
              {investor.connectorList?.map((i, idx) => <Avatar key={`avatar-${i.firstName}-${idx}`} src={i.profileImage} className={styles.avatar} />)}
            </AvatarGroup>
          </div>
        </div>
      </Grid>
    </Grid>
  );
  const { role } = userInfo;

  return (
    <Accordion
      key={name}
      classes={accordionStyles}
      expanded={isExpanded}
      elevation={0}
      TransitionProps={{
        timeout: 0,
        unmountOnExit: true
      }}
    >
      <AccordionSummary
        id='matched-investor-1'
        className={styles.summary}
        expandIcon={<ExpandMoreIcon className='expand-arrow' />}
        IconButtonProps={{
          onClick: toggleExpandedState
        }}
      >
        <Grid container wrap='nowrap' spacing={0} alignItems='center' justify='space-between' onClick={handlePagination(investorName)}>
          <Grid item xs={(matchListType === MatchListType.sent ? 2 : 3)}>
            <div className={styles.itemContentFlexLeft}>
              <Avatar src={investor.profileImage} onClick={toggleExpandedState} className={styles.avatar} />
              <StyledText variant='h3' component='div'><div className={styles.investorNameBox} style={{ hyphens: 'auto' }}>{investor.contactName}</div></StyledText>
            </div>
          </Grid>
          { (role === Role.Connector || matchListType === MatchListType.sent)
            ? (
              <Grid item xs={2}>
                <StyledText component='div'><div style={{ hyphens: 'auto', padding: '0px 20px', overflowWrap: 'break-word' }}>{investor.companyName}</div></StyledText>
              </Grid>
            ) : null }
          {role === Role.Connector && (
          <Grid item xs={2}>
            <div className={styles.itemContentFlex}>
              {!!investor.linkedinProfile && (
              <IconButton onClick={handleLinkedin}>
                <LinkedinIcon />
              </IconButton>
              )}
            </div>
          </Grid>
          )}
          <Grid item xs={2} zeroMinWidth onClick={toggleExpandedState}>
            <StyledText noWrap={false} variant='subtitle3' align='center'>
              <span className={styles.matchPercent}>{`${matchScore}%`}</span>
              <span className={styles.matchSpan}>match</span>
            </StyledText>
          </Grid>
          {role === Role.Ent && matchListType !== MatchListType.sent && ConnectorInfoGrid}
          <Button variant='text' onClick={handleViewProfile}>View profile</Button>
          {role === Role.Connector && matchListType !== MatchListType.sent
          && (
          <Button style={{ margin: '0 20px', minWidth: 142 }} onClick={handleReferralClick}>Make referral</Button>
          )}
          {matchListType === MatchListType.sent && statusToShow
          && (
          <StatusChip style={{ margin: '0 20px', flex: '0 0 100px' }} status={statusToShow} />
          )}
        </Grid>
      </AccordionSummary>
      <AccordionDetails className={styles.detail}>
        {investor.entMessage != null && role === Role.Connector && (
          <div>
            <StyledText variant='h3' style={{ marginLeft: 10 }}>Referral Request Message</StyledText>
            <StyledText style={{ marginLeft: 10, marginBottom: 20 }}><DetectLinks content={`"${investor.entMessage}"`} /></StyledText>
          </div>
        )}
        <div>
          <StyledText variant='h3' style={{ marginLeft: 10 }}>About Investor</StyledText>
          <StyledText style={{ marginLeft: 10 }}><DetectLinks content={investor.intro || ''} /></StyledText>
          <Button variant='text' color='secondary' onClick={() => history.push(`/investor/${investor.id}`, { from: window.location.pathname })}>
            See more in investor profile
          </Button>
          <StyledText variant='h3' style={{ marginLeft: 10, marginTop: 20 }}>
            Investor Investment Criteria vs Entrepreneur Information
          </StyledText>
        </div>
        { entrepreneurProfile && <InvestmentCriteriaTable entrepreneur={entrepreneurProfile} investor={investor} /> }
        {!!investor.otherCriteria && (
          <div>
            <StyledText variant='h3' style={{ marginLeft: 10, marginTop: 34 }}>Other Criteria</StyledText>
            <StyledText variant='body1' style={{ marginLeft: 10, marginTop: 7, marginBottom: 34 }}>{investor.otherCriteria}</StyledText>
          </div>
        )}
        {role === Role.Ent && (
          <ConnectorTable
            investorName={investor.contactName}
            investorId={investor.id}
            connectorList={investor.connectorList || []}
            listType={matchListType}
            refreshHandler={extendedRefreshHandler}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default InvestorAccordion;
